import { useEffect } from 'react'
import { useStore } from '../../store/useStore'
import RegimeCard from './RegimeCard'
import IndicatorPanel from './IndicatorPanel'
import EconomicCalendar from './EconomicCalendar'
import CurrencyStrengthChart from './CurrencyStrengthChart'
import AccuracyPanel from './AccuracyPanel'
import SentimentPanel from './SentimentPanel'
import StrategistPanel from './StrategistPanel'
import PositionSizingForm from './PositionSizingForm'
import PortfolioSizingPanel from './PortfolioSizingPanel'

function resolveMarket(symbol: string, symbols: ReturnType<typeof useStore.getState>['symbols']): 'fx' | 'stock' | 'crypto' | 'unknown' {
  if (!symbols) return 'unknown'
  if (symbols.fx.includes(symbol))     return 'fx'
  if (symbols.stock.includes(symbol))  return 'stock'
  if (symbols.crypto.includes(symbol)) return 'crypto'
  return 'unknown'
}

export default function AnalysisPanel() {
  const {
    selectedSymbol,
    selectedInterval,
    symbols,
    regimes,
    indicators,
    predictions,
    calendar,
    calendarLoading,
    currencyStrength,
    sentiment,
    sentimentLoading,
    strategist,
    strategistLoading,
    activeSignal,
    metadata,
    loadRegime,
    loadIndicators,
    loadCalendar,
    loadCurrencyStrength,
    loadSentiment,
    loadStrategist,
    loadActiveSignal,
  } = useStore()

  const sym        = selectedSymbol ?? ''
  const ivKey      = `${sym}_${selectedInterval}`
  const market     = resolveMarket(sym, symbols)
  // 通貨強度メーターは FX のみ表示（株式・暗号通貨では不要）
  const showStrength = market === 'fx'

  useEffect(() => {
    if (!sym) return
    loadRegime(sym)
    loadIndicators(sym, selectedInterval)
    loadSentiment(sym)
    loadStrategist(sym)
    loadActiveSignal(sym)

    // AI軍師はバックエンドで4時間ごとに更新されるため、5分ごとにポーリングして最新を反映
    const strategistTimer = setInterval(() => loadStrategist(sym), 5 * 60 * 1000)
    // 実シグナルはcrypto5分/FX・株式15分ごとに更新されうるため、5分ごとにポーリング
    const signalTimer = setInterval(() => loadActiveSignal(sym), 5 * 60 * 1000)
    return () => {
      clearInterval(strategistTimer)
      clearInterval(signalTimer)
    }
  }, [sym, selectedInterval, loadRegime, loadIndicators, loadSentiment, loadStrategist, loadActiveSignal])

  useEffect(() => {
    loadCalendar(24)
    // FX 以外では通貨強度データをフェッチしない
    if (showStrength) loadCurrencyStrength()

    const timer = setInterval(() => {
      loadCalendar(24)
      if (showStrength) loadCurrencyStrength()
    }, 5 * 60 * 1000)

    return () => clearInterval(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStrength, loadCalendar, loadCurrencyStrength])

  const regime           = sym ? regimes[sym]      ?? null : null
  const indData          = indicators[ivKey]       ?? null
  const sentimentData    = sym ? sentiment[sym]    ?? null : null
  const strategistData   = sym ? strategist[sym]   ?? null : null
  const activeSignalData = sym ? activeSignal[sym] ?? null : null
  const predData         = predictions[ivKey]      ?? null

  const symbolName = sym && metadata[sym] ? metadata[sym] : null

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* 銘柄ヘッダー */}
      {sym ? (
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-trading-text">{sym}</span>
          {symbolName && (
            <span className="text-sm text-trading-muted">{symbolName}</span>
          )}
        </div>
      ) : (
        <p className="text-sm text-trading-muted">銘柄を選択してください</p>
      )}

      {/* AI軍師ブリーフィング（最上段に強調表示） */}
      {sym && (
        <Section title="AI コンテキスト分析">
          <StrategistPanel
            data={strategistData}
            signal={activeSignalData}
            loading={strategistLoading && !strategistData}
          />
        </Section>
      )}

      {/* ポジション計算機（単一銘柄） */}
      {sym && (
        <PositionSizingForm
          signal={activeSignalData}
          currentPrice={predData?.current_price ?? null}
          symbol={sym}
        />
      )}

      {/* 分散ポジション計算機（複数ペア） */}
      <PortfolioSizingPanel />

      {/* 上段: レジーム + テクニカル指標 + カレンダー */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 左カラム: レジーム + テクニカル指標 */}
        <div className="flex flex-col gap-4 min-w-0">
          <Section title="市場レジーム">
            <RegimeCard regime={regime} loading={!regime && !!sym} />
          </Section>

          <Section title={`テクニカル指標 — ${sym || '—'} (${selectedInterval})`}>
            <IndicatorPanel data={indData} loading={!indData && !!sym} />
          </Section>
        </div>

        {/* 右カラム: 経済カレンダー（全市場）+ 通貨強度（FX のみ） */}
        <div className="flex flex-col gap-4 min-w-0">
          <Section title="経済カレンダー（今後 24 時間）">
            <EconomicCalendar data={calendar} loading={calendarLoading} symbol={sym || undefined} />
          </Section>

          {showStrength && (
            <Section title="通貨強度メーター">
              <CurrencyStrengthChart data={currencyStrength} loading={!currencyStrength} />
            </Section>
          )}

          <Section title="ニュースセンチメント (FinBERT)">
            <SentimentPanel data={sentimentData} loading={sentimentLoading && !sentimentData} />
          </Section>
        </div>
      </div>

      {/* 下段: AI 予測精度レポート（全幅） */}
      <AccuracyPanel />
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-[11px] text-trading-muted uppercase tracking-widest mb-2 px-0.5">
        {title}
      </h2>
      {children}
    </div>
  )
}
